import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Spinner } from 'react-bootstrap';
import notificacionService from '../services/notificacionService';
import { iconoPorOrigen } from '../utils/notificacionUtils';
import PageHeader from '../components/ui/PageHeader';
import '../style/pages/HomePage.css';

const formatearFecha = (fechaStr) => {
    if (!fechaStr) return '-';
    const [year, month, day] = fechaStr.split('T')[0].split('-');
    return `${day}/${month}/${year}`;
};

const formatearHora = (fechaStr) => {
    if (!fechaStr) return '';
    const fecha = new Date(fechaStr);
    if (Number.isNaN(fecha.getTime())) return '';
    return fecha.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
};

const NotificacionesPage = () => {
    const navigate = useNavigate();
    const [notificaciones, setNotificaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filtro, setFiltro] = useState('TODAS'); // 'TODAS' | 'NO_LEIDAS'

    const cargarNotificaciones = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await notificacionService.getHistorial();
            const data = response?.data ?? response;
            const lista = Array.isArray(data) ? data : [];
            lista.sort((a, b) => new Date(b.fechaProgramada) - new Date(a.fechaProgramada));
            setNotificaciones(lista);
        } catch (err) {
            console.error('Error al cargar el historial de notificaciones:', err);
            setError('No se pudo cargar el historial de notificaciones.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        cargarNotificaciones();
    }, [cargarNotificaciones]);

    const noLeidasCount = notificaciones.filter((n) => !n.leido).length;
    const visibles = filtro === 'NO_LEIDAS'
        ? notificaciones.filter((n) => !n.leido)
        : notificaciones;

    // Igual que NotificacionCampana.abrirNotificacion: marca como leída al tocar
    // (optimista, sin esperar la respuesta) y navega directo si hay trámite asociado,
    // ya que el backend propaga idTramite para evitar un fetch extra.
    const handleClickNotificacion = (notif) => {
        if (!notif.leido) {
            setNotificaciones((prev) =>
                prev.map((n) => (n.id === notif.id ? { ...n, leido: true } : n))
            );
            notificacionService.marcarComoLeidaUsuario(notif.id).catch((err) =>
                console.error('Error al marcar notificación como leída:', err)
            );
        }
        if (notif.idTramite) {
            navigate(`/tramites/${notif.idTramite}`);
        } else if (notif.cuilClienteAnses) {
            navigate(`/herramientas/seguimiento-expedientes/cliente/${encodeURIComponent(notif.cuilClienteAnses)}`);
        }
    };

    return (
        <div className="homepage-container area-admin">
            <PageHeader
                variante="plano"
                icono="bell-fill"
                titulo="Notificaciones"
                descripcion="Historial completo de tus notificaciones del sistema."
            />

            <div className="eventos-section">
                <div className="eventos-header-row">
                    <h2 className="eventos-section-title">
                        <i className="bi bi-inbox-fill me-2"></i>
                        Casilla de notificaciones
                    </h2>
                    <div className="notificaciones-filtros">
                        <button
                            type="button"
                            className={`btn-filtro-notificaciones ${filtro === 'TODAS' ? 'activo' : ''}`}
                            onClick={() => setFiltro('TODAS')}
                        >
                            Todas
                        </button>
                        <button
                            type="button"
                            className={`btn-filtro-notificaciones ${filtro === 'NO_LEIDAS' ? 'activo' : ''}`}
                            onClick={() => setFiltro('NO_LEIDAS')}
                        >
                            No leídas
                            {noLeidasCount > 0 && (
                                <span className="badge-no-leidas">{noLeidasCount}</span>
                            )}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="empty-state-eventos">
                        <i className="bi bi-exclamation-triangle"></i>
                        <h5>Ocurrió un error</h5>
                        <p>{error}</p>
                    </div>
                )}

                {!error && (loading ? (
                    <div className="loading-state">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3">Cargando notificaciones...</p>
                    </div>
                ) : visibles.length === 0 ? (
                    <div className="empty-state-eventos">
                        <i className="bi bi-bell-slash"></i>
                        <h5>{filtro === 'NO_LEIDAS' ? '¡Todo al día!' : 'Sin notificaciones'}</h5>
                        <p>
                            {filtro === 'NO_LEIDAS'
                                ? 'No tenés notificaciones pendientes de leer.'
                                : 'Todavía no recibiste ninguna notificación.'}
                        </p>
                    </div>
                ) : (
                    <Row xs={1} md={2} lg={3} className="g-3">
                        {visibles.map((notif) => {
                            const { icon, color } = iconoPorOrigen(notif);
                            const leida = notif.leido === true;
                            return (
                                <Col key={notif.id}>
                                    <div
                                        className={`notificacion-card-home ${leida ? '' : 'no-leida'}`}
                                        onClick={() => handleClickNotificacion(notif)}
                                    >
                                        <div className="notificacion-card-home-header">
                                            <div
                                                className="notificacion-card-home-icono"
                                                style={{ color: leida ? '#94a3b8' : color, background: `${leida ? '#94a3b8' : color}18` }}
                                            >
                                                <i className={`bi ${icon}`}></i>
                                            </div>
                                            {!leida && <span className="notificacion-dot-no-leida" />}
                                        </div>

                                        <p className="notificacion-mensaje-home">{notif.mensaje}</p>

                                        {notif.cuerpoTexto && (
                                            <p className="notificacion-cuerpo-home">{notif.cuerpoTexto}</p>
                                        )}

                                        <div className="notificacion-meta-home">
                                            <span className="info-item-home">
                                                <i className="bi bi-calendar3 me-2"></i>
                                                {formatearFecha(notif.fechaProgramada)}
                                            </span>
                                            <span className="info-item-home">
                                                <i className="bi bi-clock me-2"></i>
                                                {formatearHora(notif.fechaProgramada)}
                                            </span>
                                        </div>
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>
                ))}
            </div>
        </div>
    );
};

export default NotificacionesPage;
